/**
 * The invitation door, `/invite/<token>/verify` (ORDR-61).
 *
 * paio-auth builds this address from `identity.callback_hosts[0]` in
 * `manifests/ordering.yaml`, so every ordering invitation email points here.
 * Until this screen existed the catch-all served the ordering shell instead,
 * and the invitee was never verified: measured in production, the row stayed
 * `status=email_sent`, `accepted_at=null`, with no membership and no grant.
 *
 * TWO ANONYMOUS REQUESTS AND NOTHING ELSE. The read describes the invitation,
 * the POST verifies the email. Both are anonymous by design, because the person
 * reading this screen has no session yet and cannot have one. ACCEPT IS NOT
 * HERE: paio-auth matches the signed-in email against the invited one, so it
 * belongs after the round trip, in `AuthCallback`. This screen's last act is to
 * leave the token where that screen will find it.
 *
 * Served outside `OrderingContextProvider` (see `Root.tsx`), like the other two
 * auth screens: inside it, an anonymous visitor gets the staff door's notice
 * rather than the invitation.
 *
 * THE HAND-OFF IS A DOCUMENT LOAD, for the same reason the callback's is. The
 * provider fixes its door at mount, so an in-page swap would keep the anonymous
 * answer it started with.
 */

import { useCallback, useEffect, useState, type ReactElement } from "react";
import { API_BASE_URL } from "../auth/config.js";
import { writePendingInvitation } from "../auth/pendingInvitation.js";
import { DEFAULT_RETURN_PATH, loginHref } from "../auth/returnPath.js";

type State = "loading" | "ready" | "expired" | "revoked" | "error";

interface Invitation {
  readonly email?: string;
  readonly platform_role_slug?: string;
  readonly status?: string;
  readonly expires_at?: string;
}

export interface InviteVerifyProps {
  /** The token from the address. */
  readonly token: string;
  /** Injected for tests. Defaults to replacing the document. */
  readonly navigate?: (path: string) => void;
}

function replaceDocument(path: string): void {
  window.location.replace(path);
}

function invitationUrl(token: string, suffix = ""): string {
  return `${API_BASE_URL}/auth/invitations/${token}/${suffix}`;
}

/** Expired by its own status, or by an expiry that has already passed. */
function stateFor(invitation: Invitation): State {
  if (invitation.status === "revoked") {
    return "revoked";
  }
  if (invitation.status === "expired") {
    return "expired";
  }
  const expires = invitation.expires_at ? Date.parse(invitation.expires_at) : NaN;
  if (!Number.isNaN(expires) && expires < Date.now()) {
    return "expired";
  }
  return "ready";
}

export function InviteVerify({ token, navigate = replaceDocument }: InviteVerifyProps): ReactElement {
  const [state, setState] = useState<State>("loading");
  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const response = await fetch(invitationUrl(token));
        const body = (await response.json()) as { data?: Invitation; detail?: string };
        if (!live) return;
        if (!response.ok || !body.data) {
          setMessage(body.detail ?? "This invitation could not be found.");
          setState("error");
          return;
        }
        setInvitation(body.data);
        setState(stateFor(body.data));
      } catch {
        if (!live) return;
        setMessage("Could not reach the server. Please try again.");
        setState("error");
      }
    })();
    return () => {
      live = false;
    };
  }, [token]);

  const accept = useCallback(async (): Promise<void> => {
    setBusy(true);
    try {
      const response = await fetch(invitationUrl(token, "verify"), { method: "POST" });
      const body = (await response.json()) as {
        data?: { verified?: boolean; status?: string };
        detail?: string;
      };
      if (!response.ok || !(body.data?.verified || body.data?.status === "accepted")) {
        setMessage(body.detail ?? "This invitation could not be verified.");
        setState("error");
        return;
      }
      // Only now, and only on success: the callback must never replay a token
      // whose verify did not land.
      writePendingInvitation(token);
      navigate(loginHref(DEFAULT_RETURN_PATH));
    } catch {
      setMessage("Could not reach the server. Please try again.");
      setState("error");
    } finally {
      setBusy(false);
    }
  }, [token, navigate]);

  return (
    <main className="flex min-h-screen items-center justify-center p-4">
      <section className="w-full max-w-md rounded-2xl border border-(--color-border) bg-(--surface-elevated) p-8">
        {state === "loading" && (
          <p data-testid="invite-loading" className="text-center text-(--color-text-secondary)">
            Loading your invitation...
          </p>
        )}

        {state === "ready" && (
          <>
            <h1 className="mb-3 text-2xl font-semibold text-(--color-text-primary)">
              You have been invited
            </h1>
            <p className="mb-6 text-(--color-text-secondary)">
              This invitation was sent to{" "}
              <strong className="text-(--color-text-primary)">{invitation?.email}</strong>. Accept
              it and sign in with that address to get started.
            </p>
            <button
              type="button"
              onClick={() => void accept()}
              disabled={busy}
              data-testid="invite-accept"
              className="w-full cursor-pointer rounded-md bg-(--color-primary-700) py-3 font-medium text-white transition-colors hover:bg-(--color-primary-800) disabled:cursor-not-allowed disabled:opacity-60"
            >
              {busy ? "Accepting..." : "Accept and continue"}
            </button>
          </>
        )}

        {state === "expired" && (
          <div data-testid="invite-expired" className="text-center">
            <h1 className="mb-3 text-2xl font-semibold text-(--color-text-primary)">
              This invitation has expired
            </h1>
            <p className="text-(--color-text-secondary)">
              Ask whoever invited you to send a new one.
            </p>
          </div>
        )}

        {state === "revoked" && (
          <div data-testid="invite-revoked" className="text-center">
            <h1 className="mb-3 text-2xl font-semibold text-(--color-text-primary)">
              This invitation has been withdrawn
            </h1>
            <p className="text-(--color-text-secondary)">
              Ask whoever invited you if you still need access.
            </p>
          </div>
        )}

        {state === "error" && (
          <div data-testid="invite-error" className="text-center">
            <h1 className="mb-3 text-2xl font-semibold text-(--color-text-primary)">
              Something went wrong
            </h1>
            <p className="text-(--color-text-secondary)">{message}</p>
          </div>
        )}
      </section>
    </main>
  );
}
